import AxiosInstance from "../../shared/utils/axios";
import type { AxiosResponse } from "axios";
import { notifyProtocolTemplatesAvailabilityChanged } from "./protocolTemplatesAvailability";

interface ProtocolTemplateObjective {
  targets: string[];
}

interface ProtocolTemplate {
  id: string;
  name: string;
  created_at: string;
  protocol_type: string[];
  objectives: ProtocolTemplateObjective[];
  crop: string;
  protocols_count: number;
  actions: string;
}

interface ProtocolTemplateApiResponse {
  id: string;
  template_name: string;
  created_at: string;
  trial_types: string[];
  target: string[];
  crop: string;
  protocols_created_count: number;
}

interface ProtocolTemplateListApiResponse {
  data?: ProtocolTemplateApiResponse[];
  content?: ProtocolTemplateApiResponse[];
  templates?: ProtocolTemplateApiResponse[];
  results?: ProtocolTemplateApiResponse[];
  count?: number;
  total?: number;
  total_count?: number;
  totalCount?: number;
}

export interface FetchTemplatesResponse {
  templates: ProtocolTemplate[];
  total: number;
}

type ProtocolTemplateListAxiosResponse = AxiosResponse<
  ProtocolTemplateApiResponse[] | ProtocolTemplateListApiResponse
> & {
  total?: number;
};

export type ProtocolTemplateType = "universal" | "workspace";

export interface CreateProtocolTemplatePayload {
  protocol_id: string;
  template_name: string;
  template_type: ProtocolTemplateType;
  workspace_id?: string;
}

export interface CreateProtocolTemplateResponse {
  id: string;
  protocol_id: string;
  template_name: string;
  template_type: ProtocolTemplateType;
  workspace_id: string | null;
  org_id: string;
  archived: boolean;
  created_at: string;
  updated_at: string;
}

interface ProtocolTemplateDropdownApiResponse {
  id: string;
  template_name: string;
}

export interface ProtocolTemplateDropdownItem {
  id: string;
  name: string;
}

/**
 * Fetches the full data payload of a protocol template to pre-populate the create-protocol form.
 *
 * @param templateId Template identifier to retrieve data for.
 * @param orgId Organization identifier sent in request headers.
 * @param workspaceId Workspace identifier sent in request headers.
 * @param countryCode When provided, asks the backend to resolve the template's
 * crop/product data for this specific country (sent as `country_code` and
 * `org_id` query params). Used to re-resolve the template on country change.
 * @returns The template data payload returned by the API.
 */
export const fetchProtocolTemplateData = async (
  templateId: string,
  orgId: string,
  workspaceId: string,
  countryCode?: string
): Promise<any> => {
  const response = await AxiosInstance.get(
    `/template/protocol/${templateId}/data`,
    {
      headers: {
        "org-id": orgId,
        "workspace-id": workspaceId,
      },
      params: countryCode
        ? { country_code: countryCode, org_id: orgId }
        : undefined,
    }
  );

  return response.data;
};

const normalizeTemplateObjectives = (
  targets: ProtocolTemplateApiResponse["target"] | undefined
): ProtocolTemplateObjective[] => {
  if (!Array.isArray(targets)) {
    return [];
  }

  const normalizedTargets = targets.filter(
    (target): target is string =>
      typeof target === "string" && target.length > 0
  );

  return normalizedTargets.length > 0 ? [{ targets: normalizedTargets }] : [];
};

/**
 * Fetches protocols for either an organization or a workspace with pagination and filters.
 *
 * @param orgId Organization identifier used for request headers and org-scoped queries.
 * @param page Zero-based page index for paginated results.
 * @param limit Maximum number of protocols to return per page.
 * @param archived Whether to fetch archived protocols.
 * @param search Free-text search term applied to protocol results.
 * @param status Status filter applied to protocol results.
 * @param isWorkspace Whether the request should use workspace scope instead of organization scope.
 * @param workSpaceId Workspace identifier used for request headers and workspace-scoped queries.
 * @returns The protocol list payload returned by the API.
 */
export const fetchProtocols = async (
  orgId: string,
  page: number = 0,
  limit: number = 10,
  archived: boolean = false,
  search: string = "",
  status: string = "",
  isWorkspace: boolean,
  workSpaceId: string
): Promise<any> => {
  const paramKey = isWorkspace ? "workspace_id" : "org_id";

  const response = await AxiosInstance.get(
    `/protocols?${paramKey}=${
      isWorkspace ? workSpaceId : orgId
    }&page=${page}&limit=${limit}&archived=${archived.toString()}&search=${search}&status=${status}`,
    {
      headers: {
        "org-id": orgId,
        "workspace-id": workSpaceId,
      },
    }
  );

  return response.data;
};

/**
 * Updates the archived state of a protocol.
 *
 * @param protocolId Protocol identifier to update.
 * @param archived Whether the protocol should be archived.
 * @param orgId Organization identifier sent in request headers.
 * @param workSpaceId Workspace identifier sent in request headers.
 * @returns A promise that resolves when the archive state is updated.
 */
export const toggleArchiveProtocol = async (
  protocolId: string,
  archived: boolean = false,
  orgId: string,
  workSpaceId: string
): Promise<void> => {
  await AxiosInstance.patch(
    `/protocol/${protocolId}`,
    { archived },
    {
      headers: {
        "org-id": orgId,
        "workspace-id": workSpaceId,
      },
    }
  );
};

/**
 * Removes the archived flag from a protocol.
 *
 * @param protocolId Protocol identifier to update.
 * @param orgId Organization identifier sent in request headers.
 * @param workSpaceId Workspace identifier sent in request headers.
 * @returns A promise that resolves when the protocol is unarchived.
 */
export const unarchiveProtocol = async (
  protocolId: string,
  orgId: string,
  workSpaceId: string
): Promise<void> => {
  await AxiosInstance.patch(
    `/protocol/${protocolId}`,
    { archived: false },
    {
      headers: {
        "org-id": orgId,
        "workspace-id": workSpaceId,
      },
    }
  );
};

/**
 * Deletes a protocol.
 *
 * @param protocolId Protocol identifier to delete.
 * @param orgId Organization identifier sent in request headers.
 * @param workSpaceId Workspace identifier sent in request headers.
 * @returns A promise that resolves when the protocol is deleted.
 */
export const deleteProtocol = async (
  protocolId: string,
  orgId: string,
  workSpaceId: string
): Promise<void> => {
  await AxiosInstance.delete(`/protocol/${protocolId}`, {
    headers: {
      "org-id": orgId,
      "workspace-id": workSpaceId,
    },
  });
};

/**
 * Returns the mock protocol templates used by the protocols UI.
 *
 * @returns The available template list.
 */
export const fetchTemplates = async (
  orgId: string,
  page: number = 0,
  limit: number = 10,
  archived: boolean = false,
  search: string = "",
  status: string = "",
  isWorkspace: boolean,
  workSpaceId: string
): Promise<FetchTemplatesResponse> => {
  const paramKey = isWorkspace ? "workspace_id" : "org_id";

  const response = await AxiosInstance.get<
    ProtocolTemplateApiResponse[] | ProtocolTemplateListApiResponse,
    ProtocolTemplateListAxiosResponse
  >(
    `/template/protocol/list?${paramKey}=${
      isWorkspace ? workSpaceId : orgId
    }&page=${page}&limit=${limit}&archived=${archived.toString()}&search=${search}&trial_type=${status}`,
    {
      headers: {
        "org-id": orgId,
        "workspace-id": workSpaceId,
      },
    }
  );

  const templates = Array.isArray(response.data)
    ? response.data
    : response.data.data ??
      response.data.content ??
      response.data.templates ??
      response.data.results ??
      [];
  const total = Array.isArray(response.data)
    ? response.total ?? response.data.length
    : response.total ??
      response.data.count ??
      response.data.total ??
      response.data.total_count ??
      response.data.totalCount ??
      templates.length;

  return {
    templates: templates.map((template) => ({
      id: template.id,
      name: template.template_name,
      created_at: template.created_at,
      protocol_type: template.trial_types,
      objectives: normalizeTemplateObjectives(template.target),
      crop: template.crop,
      protocols_count: template.protocols_created_count,
      actions: "",
    })),
    total,
  };
};

/**
 * Deletes a template.
 *
 * @param templateId Template identifier to delete.
 * @param orgId Organization identifier sent in request headers.
 * @param workSpaceId Workspace identifier sent in request headers.
 * @returns A promise that resolves when the template is deleted.
 */
export const deleteTemplate = async (
  templateId: string,
  orgId: string,
  workSpaceId: string
): Promise<void> => {
  await AxiosInstance.delete(`/template/protocol/${templateId}`, {
    headers: {
      "org-id": orgId,
      "workspace-id": workSpaceId,
    },
  });

  notifyProtocolTemplatesAvailabilityChanged();
};

/**
 * Creates a protocol template from a published protocol snapshot.
 *
 * @param payload Template creation payload.
 * @param orgId Organization identifier sent in request headers.
 * @param workSpaceId Workspace identifier sent in request headers.
 * @returns The created template record.
 */
export const createProtocolTemplate = async (
  payload: CreateProtocolTemplatePayload,
  orgId: string,
  workSpaceId: string
): Promise<CreateProtocolTemplateResponse> => {
  const response = await AxiosInstance.post<CreateProtocolTemplateResponse>(
    "/template/protocol",
    payload,
    {
      headers: {
        "org-id": orgId,
        "workspace-id": workSpaceId,
      },
    }
  );

  notifyProtocolTemplatesAvailabilityChanged();

  return response.data;
};

/**
 * Fetches protocol templates for create-protocol modal dropdown.
 *
 * @param orgId Organization identifier sent in request headers.
 * @param workspaceId Workspace identifier used in query and request headers.
 * @returns A normalized list for dropdown selection.
 */
export const fetchProtocolTemplateDropdown = async (
  orgId: string,
  workspaceId: string
): Promise<ProtocolTemplateDropdownItem[]> => {
  const response = await AxiosInstance.get<
    ProtocolTemplateDropdownApiResponse[]
  >(`/template/protocol/dropdown?workspace_id=${workspaceId}`, {
    headers: {
      "org-id": orgId,
      "workspace-id": workspaceId,
    },
  });

  return (Array.isArray(response.data) ? response.data : []).map(
    (template) => ({
      id: template.id,
      name: template.template_name,
    })
  );
};
